import { createDeepEqualSelector, RootState } from '@/store/store';
import { HypertrackerState } from '@/state/hypertracker/hypertrackerTypes';

const hypertrackerSelector = (state: RootState): HypertrackerState => state.hypertracker;

export const selectSegmentData = createDeepEqualSelector([hypertrackerSelector], (hypertracker) => {
    return hypertracker.segmentsSummary;
});
export const selectSegmentsBiasMetrics = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.segmentsBiasMetrics;
    },
);

export const selectNextUpdate = createDeepEqualSelector([hypertrackerSelector], (hypertracker) => {
    return hypertracker.segmentsSummary?.nextUpdate || null;
});

export const selectHypertrackerFilters = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.filters;
    },
);

export const selectWalletFilters = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.filters.wallets;
    },
);

export const selectSegmentSummaryById = createDeepEqualSelector(
    [hypertrackerSelector, (state, props: { segmentId: number }) => props.segmentId],
    (hypertracker, segmentId) => {
        return hypertracker?.singleSegmentSummary?.[segmentId] || null;
    },
);

export const selectSegmentLargestPositionsById = createDeepEqualSelector(
    [hypertrackerSelector, (state, props: { segmentId: number }) => props.segmentId],
    (hypertracker, segmentId) => {
        return hypertracker?.largestPositions?.[segmentId] || null;
    },
);

export const selectSegmentLiquidationRiskById = createDeepEqualSelector(
    [hypertrackerSelector, (state, props: { segmentId: number }) => props.segmentId],
    (hypertracker, segmentId) => {
        return hypertracker?.liquidationRisk?.[segmentId] || null;
    },
);

export const selectSegmentWalletsById = createDeepEqualSelector(
    [hypertrackerSelector, (state, props: { segmentId: number }) => props.segmentId],
    (hypertracker, segmentId) => {
        return hypertracker?.wallets?.[segmentId] || null;
    },
);
export const selectModalWindow = createDeepEqualSelector([hypertrackerSelector], (hypertracker) => {
    return hypertracker.modalWindow;
});
export const selectIsLoadingDisplayX = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.isLoadingDisplayX;
    },
);

export const selectHeatmapCellDetails = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.heatmapCellDetails;
    },
);

export const selectHeatmapSelectedRow = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.heatmapSelectedRow;
    },
);
export const selectHeatmapSelectedField = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.heatmapSelectedField;
    },
);

export const selectTempKeyData = createDeepEqualSelector([hypertrackerSelector], (hypertracker) => {
    return hypertracker.tempKeyData;
});

export const selectConnections = createDeepEqualSelector([hypertrackerSelector], (hypertracker) => {
    return hypertracker.connections;
});

export const selectVaults = createDeepEqualSelector([hypertrackerSelector], (hypertracker) => {
    return hypertracker.vaults;
});

export const selectFavoritePositions = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        const positions = [...hypertracker.favoritePositions];
        if (positions.length > 0) {
            positions.sort((a, b) => {
                if (a.favorite?.featured && !b.favorite?.featured) return -1;
                if (!a.favorite?.featured && b.favorite?.featured) return 1;
                return 0;
            });
        }
        return positions;
    },
);
export const selectFavoriteAccounts = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        const accounts = [...hypertracker.favoriteAccounts];
        if (accounts.length > 0) {
            accounts.sort((a, b) => {
                if (a.favorite?.featured && !b.favorite?.featured) return -1;
                if (!a.favorite?.featured && b.favorite?.featured) return 1;
                return 0;
            });
        }
        return accounts;
    },
);

export const selectFavoriteIdsHolderObject = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.favoriteIdsHolderObject;
    },
);

export const selectFeaturedAccounts = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.featuredAccounts;
    },
);

export const selectFeaturedPositions = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.featuredPositions;
    },
);

export const selectFavoriteLimits = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.favoriteLimits;
    },
);
export const selectStatsMetrics = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.statsMetrics;
    },
);
export const selectStatsExposeRatio = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.statsExposeRatio;
    },
);
export const selectStatsMetricByHype = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.statsMetricByHype;
    },
);
export const selectStatsActivePerpTraders = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.statsActivePerpTraders;
    },
);
export const selectStatsTotalWalletsOnHL = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.statsTotalWalletsOnHL;
    },
);
export const selectStatsWalletCreationRate = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.statsWalletCreationRate;
    },
);
export const selectStartTotalWalletsEquity = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.startTotalWalletsEquity;
    },
);
export const selectStatsEquityDistributionBuckets = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.statsEquityDistributionBuckets;
    },
);
export const selectPerpsCurrentAsset = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.perpsCurrentAsset;
    },
);
export const selectPerpsAssetsData = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.perpsAssetsData;
    },
);
export const selectPositionsToExplore = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.perpsPositionsToExplore;
    },
);
export const selectPositionExplorerFilters = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.positionExplorerFilters;
    },
);

export const selectLeaderboardsTableFilters = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.leaderboardsTableFilters;
    },
);

export const selectBuildersDailyAnalytics = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.buildersDailyAnalytics;
    },
);
export const selectBuildersAllTimeAnalytics = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.buildersAllTimeAnalytics;
    },
);
export const selectBuildersAllTime = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.buildersAllTime;
    },
);
export const selectBuilders24Hours = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.builders24h;
    },
);
export const selectBuildersByPeriod = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.buildersByPeriod;
    },
);

export const selectTopBuildersRevenue = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.topBuildersRevenue;
    },
);

export const selectBuilderV2 = createDeepEqualSelector([hypertrackerSelector], (hypertracker) => {
    return hypertracker.builderV2;
});
export const selectUsersForBuilder = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.usersForBuilder;
    },
);
export const selectBuilderMainChartView = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.builderMainChartView;
    },
);
export const selectBuildersTableRange = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.buildersTableRange;
    },
);
export const selectPersonalBuilderRange = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.personalBuilderRange;
    },
);

export const selectTotalTraders = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker?.segmentsSummary?.countTraders || 0;
    },
);

export const selectLeaderboardData = createDeepEqualSelector(
    [hypertrackerSelector, (_, props: { key: string }) => props.key],
    (hypertracker, key) => {
        return hypertracker?.leaderboards?.[key] || null;
    },
);
